import React from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {Path, Svg} from 'react-native-svg';

import theme from '../../../style/theme';
import Text from '../../ui-controls/Text';

import {SvgBadgeComponent} from './types';

const badge = theme.size.badge;

// Chevron stretches to match badge width via alignSelf + viewBox scaling.
// Uses alignSelf: 'stretch' instead of width: '100%' because Yoga resolves
// percentage widths to 0 when the parent has no definite size (shrink-to-fit).
const BmSnsBadgeBottomImg = () => (
  <Svg
    style={{alignSelf: 'stretch', height: badge.chevronHeight}}
    viewBox="0 0 100 100"
    preserveAspectRatio="none">
    <Path d="M0 0 L50 100 L100 0 Z" fill={badge.color} />
  </Svg>
);

// Kept as export for Badge index default
export const DEFAULT_BADGE_SIZE = 58;

/**
 * Title font size derived from label length, mirroring HbTcBadge.
 * The badge is fixed-geometry (capped at a percentage of the tile), so the
 * label has to be sized deterministically: adjustsFontSizeToFit shrinks the
 * measured size on Android without recomputing the ascent, which clips glyphs.
 */
const titleSizeForLabel = (label: string) => {
  if (label.length > 14) return badge.titleFontSizeLong - 1;
  if (label.length > 11) return badge.titleFontSizeLong;
  return badge.titleFontSize;
};

const BmSnsBadge: SvgBadgeComponent = ({priceDollars, priceCents, text}) => {
  const titleFontSize = titleSizeForLabel(text);
  return (
    <View style={styles.badgeRoot} testID="badge">
      <View style={styles.badgeContent} testID="badge-content">
        <View style={styles.badgeTitle} testID="badge-title">
          <Text
            style={[styles.badgeTitleText, {fontSize: titleFontSize}]}
            testID="badge-title-text"
            numberOfLines={1}
            allowFontScaling={false}
            adjustsFontSizeToFit
            minimumFontScale={0.5}>
            {text}
          </Text>
        </View>

        <View style={styles.badgePriceContainer} testID="badge-price-container">
          <View style={styles.badgePrice} testID="badge-price">
            <Text
              style={styles.badgePriceDollarSignText}
              testID="badge-price-dollar-sign-text"
              allowFontScaling={false}>
              $
            </Text>
            <Text
              style={styles.badgePriceDollarsText}
              testID="badge-price-dollars-text"
              allowFontScaling={false}>
              {priceDollars}
            </Text>
            <Text
              style={styles.badgePriceCentsText}
              testID="badge-price-cents-text"
              allowFontScaling={false}>
              {priceCents ? '.' : ''}
              {priceCents}
            </Text>
          </View>
        </View>
      </View>
      <BmSnsBadgeBottomImg />
    </View>
  );
};

const styles = StyleSheet.create({
  // The badge shrink-wraps its label but must never exceed the width its
  // container allows, otherwise a long label is clipped rather than scaled.
  // Capping here gives the title Text a bounded width, which is what
  // adjustsFontSizeToFit needs in order to do anything at all.
  badgeRoot: {
    maxWidth: '100%',
  },
  badgeContent: {
    backgroundColor: badge.color,
    paddingHorizontal: badge.contentPadding,
    paddingTop: badge.contentPadding,
    marginBottom: -0.5,
  },
  badgeTitle: {
    alignSelf: 'stretch',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: badge.titlePaddingVertical,
    paddingHorizontal: badge.titlePaddingHorizontal,
  },
  badgeTitleText: {
    color: badge.color,
    fontWeight: '800',
    fontSize: badge.titleFontSize,
    textAlign: 'center',
    textAlignVertical: 'center',
  },
  badgePriceContainer: {
    paddingVertical: badge.pricePaddingVertical,
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgePrice: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  badgePriceDollarSignText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: badge.dollarSignFontSize,
  },
  badgePriceDollarsText: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: badge.priceFontSize,
    // @ts-expect-error - lineHeight '1' is a web CSS value
    lineHeight: Platform.select({
      web: '1',
    }),
  },
  badgePriceCentsText: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: badge.dollarSignFontSize,
  },
});

export default BmSnsBadge;
